import { Box, Divider, Stack, Typography } from '@mui/material';
import { BaseSyntheticEvent, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { useRemainingCharacters } from '@/components/form/hooks/useRemainingCharacters.hook';
import { Card } from '@/components/layout/Card/Card';
import { useFormContext } from '@/components/react-hook-form/hooks';
import { getMaxLengthValidation } from '@/components/utils/validators';
import { HelpIconButton } from '@/modules/content/components/HelpIconButton';
import { PathsOf } from '@/types/subform';

import { LuminaryDisclaimerModal } from './LuminaryDisclaimerModal';
import { StandaloneDisclaimerPreview } from './StandaloneDisclaimerPreview';

export interface LegalDisclaimersFormShape {
  presentationCoverDisclaimerTitle: string;
  presentationCoverDisclaimerBody: string;
  presentationFooterDisclaimerTitle: string;
  presentationFooterDisclaimerBody: string;
  presentationStandaloneDisclaimerTitle: string;
  presentationStandaloneDisclaimerBody: string;
  includeLuminaryDisclaimer: boolean;
}

export interface LegalDisclaimersFormShapeWithDisclaimerId
  extends LegalDisclaimersFormShape {
  disclaimerId: string | null;
}

export type FormField = PathsOf<LegalDisclaimersFormShape>;

export interface LegalDisclaimersFormProps {
  onSubmit: (
    e?: BaseSyntheticEvent<object, unknown, unknown> | undefined
  ) => Promise<void>;
  defaultDisclaimerConfiguration: LegalDisclaimersFormShape;
  readOnly?: boolean;
}

// these constants are also defined in the backend for DB schema purposes,
// so if you change them here, you need to change them there too
const BODY_MAX_LENGTH = 500;
const FOOTER_BODY_MAX_LENGTH = 750;
const HEADING_MAX_LENGTH = 100;
const STANDALONE_BODY_MAX_LENGTH = 6000;

// Builds the character-count help text shown beneath a disclaimer body input:
// "X/limit characters remaining" normally, switching to a red "X characters
// over limit" once the limit is exceeded (which also blocks saving).
function useDisclaimerBodyCharacterCount(fieldName: FormField, limit: number) {
  const { remainingExact, isOverLimit } = useRemainingCharacters(
    fieldName,
    limit
  );
  return {
    helpText: isOverLimit
      ? `${-remainingExact} characters over limit`
      : `${remainingExact}/${limit} characters remaining`,
    helpTextVariant: isOverLimit ? ('error' as const) : undefined,
  };
}

export function LegalDisclaimersForm({
  onSubmit,
  defaultDisclaimerConfiguration,
  readOnly = false,
}: LegalDisclaimersFormProps) {
  const [isLuminaryDisclaimerModalOpen, setLuminaryDisclaimerModalOpen] =
    useState(false);
  const {
    control,
    formState: { isSubmitting, isLoading },
  } = useFormContext<LegalDisclaimersFormShape>();
  const coverBodyCharacterCount = useDisclaimerBodyCharacterCount(
    'presentationCoverDisclaimerBody',
    BODY_MAX_LENGTH
  );
  const footerBodyCharacterCount = useDisclaimerBodyCharacterCount(
    'presentationFooterDisclaimerBody',
    FOOTER_BODY_MAX_LENGTH
  );
  const standaloneBodyCharacterCount = useDisclaimerBodyCharacterCount(
    'presentationStandaloneDisclaimerBody',
    STANDALONE_BODY_MAX_LENGTH
  );
  return (
    <>
      <LuminaryDisclaimerModal
        isOpen={isLuminaryDisclaimerModalOpen}
        onClose={() => setLuminaryDisclaimerModalOpen(false)}
      />
      <FormFieldsDisabledProvider isDisabled={isLoading || readOnly}>
        <Stack component="form" spacing={3} onSubmit={onSubmit}>
          <Box>
            <Box mb={2}>
              <Typography variant="title-s">Cover slide disclaimer</Typography>
              <Typography variant="body-xs">
                This will appear along the bottom of the cover slide.
              </Typography>
            </Box>
            <Stack spacing={2}>
              <FormAwareTextInput
                control={control}
                placeholder={
                  defaultDisclaimerConfiguration.presentationCoverDisclaimerTitle
                }
                label="Headline"
                fieldName={
                  `presentationCoverDisclaimerTitle` as const satisfies FormField
                }
                validation={{
                  maxLength: (value) =>
                    getMaxLengthValidation(HEADING_MAX_LENGTH)(value as string),
                }}
              />
              <FormAwareTextInput
                control={control}
                label="Content"
                rows={4}
                multiline
                placeholder={
                  defaultDisclaimerConfiguration.presentationCoverDisclaimerBody
                }
                fieldName={
                  `presentationCoverDisclaimerBody` as const satisfies FormField
                }
                helpText={coverBodyCharacterCount.helpText}
                helpTextVariant={coverBodyCharacterCount.helpTextVariant}
                validation={{
                  maxLength: (value) =>
                    getMaxLengthValidation(BODY_MAX_LENGTH)(value as string),
                }}
              />
            </Stack>
          </Box>
          <Divider />
          <Box>
            <Box mb={2}>
              <Typography variant="title-s">Footer disclaimer</Typography>
              <Typography variant="body-xs">
                This will appear in the footer of every slide (excluding the
                cover & standalone disclaimer slide).
              </Typography>
            </Box>
            <Stack spacing={2}>
              <FormAwareTextInput
                control={control}
                label="Headline"
                placeholder={
                  defaultDisclaimerConfiguration.presentationFooterDisclaimerTitle
                }
                fieldName={
                  `presentationFooterDisclaimerTitle` as const satisfies FormField
                }
                validation={{
                  maxLength: (value) =>
                    getMaxLengthValidation(HEADING_MAX_LENGTH)(value as string),
                }}
              />
              <FormAwareTextInput
                control={control}
                label="Content"
                rows={4}
                multiline
                placeholder={
                  defaultDisclaimerConfiguration.presentationFooterDisclaimerBody
                }
                fieldName={
                  `presentationFooterDisclaimerBody` as const satisfies FormField
                }
                helpText={footerBodyCharacterCount.helpText}
                helpTextVariant={footerBodyCharacterCount.helpTextVariant}
                validation={{
                  maxLength: (value) =>
                    getMaxLengthValidation(FOOTER_BODY_MAX_LENGTH)(
                      value as string
                    ),
                }}
              />
            </Stack>
          </Box>
          <Divider />
          <Box>
            <Box mb={2}>
              <Typography variant="title-s">Standalone disclaimer</Typography>
              <Typography variant="body-xs">
                Comprehensive disclaimers are included on a dedicated slide at
                the end of presentations.
              </Typography>
            </Box>
            <Stack spacing={2}>
              <FormAwareTextInput
                control={control}
                label="Headline"
                placeholder={
                  defaultDisclaimerConfiguration.presentationStandaloneDisclaimerTitle
                }
                fieldName={
                  `presentationStandaloneDisclaimerTitle` as const satisfies FormField
                }
                validation={{
                  maxLength: (value) =>
                    getMaxLengthValidation(HEADING_MAX_LENGTH)(value as string),
                }}
              />
              <FormAwareTextInput
                control={control}
                label="Content"
                rows={8}
                multiline
                resizable
                placeholder={
                  defaultDisclaimerConfiguration.presentationStandaloneDisclaimerBody
                }
                fieldName={
                  `presentationStandaloneDisclaimerBody` as const satisfies FormField
                }
                helpText={standaloneBodyCharacterCount.helpText}
                helpTextVariant={standaloneBodyCharacterCount.helpTextVariant}
                validation={{
                  maxLength: (value) =>
                    getMaxLengthValidation(STANDALONE_BODY_MAX_LENGTH)(
                      value as string
                    ),
                }}
              />
            </Stack>
          </Box>
          <Box>
            <Card variant="outlined" sx={{ p: 1 }}>
              <FormAwareCheckbox
                control={control}
                label={
                  <Stack direction="row" spacing={0.5}>
                    <span>Show Luminary disclaimer slide</span>
                    <HelpIconButton
                      buttonProps={{
                        onClick: () => setLuminaryDisclaimerModalOpen(true),
                      }}
                    />
                  </Stack>
                }
                fieldName={
                  `includeLuminaryDisclaimer` as const satisfies FormField
                }
              />
            </Card>
          </Box>
          <StandaloneDisclaimerPreview />
          {!readOnly && (
            <Stack direction="row" justifyContent="flex-end">
              <Button
                loading={isSubmitting}
                type="submit"
                variant="primary"
                size="sm"
              >
                Save changes
              </Button>
            </Stack>
          )}
        </Stack>
      </FormFieldsDisabledProvider>
    </>
  );
}
